import {
  getTourSchedulingRedirectStep,
  isTourSchedulingRedirectStep,
} from '../../../../srcLegacy/shared/containers/CreateListingPage/utils';

import {
  RELISTING_STEP_SUFFIX,
  STEP,
  STEP_DELIMITER,
} from '../../../../srcLegacy/shared/containers/CreateListingPage/constants';

import type { WizardContext } from '../types';

const DEFAULT_STEP_FOR_REACTIVATION = STEP.REVIEW;
const DEFAULT_STEP_FOR_RELISTING = STEP.UPDATE_RECENTLY_CHANGED;
const DEFAULT_STEP_FOR_MULTIPLEX_LISTING = STEP.MULTIPLEX_PROPERTY_DETAILS;

export function getDefaultStep(context: WizardContext): string | undefined {
  const { flags } = context;

  if (flags.canAccessMultiplexListingFlow) {
    return DEFAULT_STEP_FOR_MULTIPLEX_LISTING;
  }

  if (flags.canAccessRelistingFlow) {
    return DEFAULT_STEP_FOR_RELISTING;
  }

  if (flags.canAccessMLReactivationFlow) {
    return DEFAULT_STEP_FOR_REACTIVATION;
  }

  // For standard/room-for-rent flows, use first active step (determined by caller)
  return undefined;
}

export function parseRelistingStep(stepString: string): {
  step: string;
  isInRelistingReview: boolean;
} {
  const stepSplit = stepString.split(STEP_DELIMITER);

  if (stepSplit[1] === RELISTING_STEP_SUFFIX) {
    return {
      step: stepSplit[0],
      isInRelistingReview: true,
    };
  }

  return {
    step: stepString,
    isInRelistingReview: false,
  };
}

export function isValidRelistingStep(currentStepKey: string, canAccessPostingPathTourScheduling: boolean): boolean {
  // Base relisting steps
  const relistingSteps: Array<string> = [
    STEP.UPDATE_RECENTLY_CHANGED,
    STEP.CONSIDER_ADDITIONAL_DETAILS,
    STEP.ADD_PHOTOS,
    STEP.REVIEW,
  ];

  // Add tour scheduling steps if enabled
  if (canAccessPostingPathTourScheduling) {
    relistingSteps.push(
      STEP.TOUR_SCHEDULING_BOOKING_METHOD_RELIST,
      STEP.TOUR_SCHEDULING_TIMEZONE_RELIST,
      STEP.TOUR_SCHEDULING_AVAILABILITY_RELIST,
    );
  }

  return relistingSteps.includes(currentStepKey);
}

/**
 * Resolves the initial step for the wizard based on all available context
 * Matches legacy: srcLegacy/shared/containers/CreateListingPage/index.jsx lines 871-999
 *
 * Priority order:
 * 1. Reactivation flow redirect (if eligible)
 * 2. Deep link query parameter
 * 3. Saved step from API (after validation)
 * 4. Default step for flow
 *
 * When isFeed is true, shouldSave is always false — feed listings skip modularListingProgress.
 */
export function resolveInitialStep(params: {
  context: WizardContext;
  savedStep: string | undefined;
  deepLinkStep: string | undefined;
  activeSteps: Array<{ stepKey: string }>;
  isFeed: boolean;
  active: boolean;
}): { stepKey: string; shouldSave: boolean } {
  const { context, savedStep, deepLinkStep, activeSteps, isFeed, active } = params;
  const { flags } = context;
  const defaultStep = getDefaultStep(context) || activeSteps[0]?.stepKey || STEP.REVIEW;

  // Handle undefined or empty saved step
  if (!savedStep) {
    return {
      stepKey: defaultStep,
      shouldSave: !isFeed,
    };
  }

  // Parse relisting suffix if present
  const { step: parsedStep, isInRelistingReview } = parseRelistingStep(savedStep);

  // Check for reactivation flow redirect
  if (flags.canAccessMLReactivationFlow) {
    const validStepUpperCase =
      deepLinkStep || activeSteps?.find((step) => step.stepKey === parsedStep?.toUpperCase())?.stepKey || defaultStep;
    const isListingActiveAndValidStepPayAndPublish = validStepUpperCase === STEP.PAY_AND_PUBLISH && active;

    // Force redirect to review/update step if in reactivation flow
    if (
      isFeed ||
      isListingActiveAndValidStepPayAndPublish ||
      flags.canAccessMLReactivationFlow ||
      flags.canAccessRelistingFlow
    ) {
      let targetStepKey: string = STEP.REVIEW;
      let shouldRedirect = true;

      // If relisting and on review step without relisting suffix, go to UPDATE_RECENTLY_CHANGED.
      // If relisting and already past first visit (valid saved step), continue from where user left off
      if (flags.canAccessRelistingFlow) {
        if (parsedStep === STEP.REVIEW.toLowerCase() && !isInRelistingReview) {
          targetStepKey = STEP.UPDATE_RECENTLY_CHANGED;
        } else {
          shouldRedirect = false;
        }
      }

      if (shouldRedirect) {
        return {
          stepKey: targetStepKey,
          shouldSave: !isFeed,
        };
      }
    }
  }

  // Handle tour scheduling redirect (reset incomplete tour flows)
  let stepToValidate = parsedStep;
  if (isTourSchedulingRedirectStep(parsedStep)) {
    const redirected = getTourSchedulingRedirectStep(parsedStep);
    if (redirected) {
      stepToValidate = redirected;
    }
  }

  // Deep link takes priority over saved step
  if (deepLinkStep) {
    return {
      stepKey: deepLinkStep,
      shouldSave: false, // Don't save deep link steps
    };
  }

  // Validate saved step exists in STEP constants
  const stepKeyFromApi = activeSteps.find((step) => step.stepKey === stepToValidate?.toUpperCase())?.stepKey;
  let validatedStep: string = stepKeyFromApi || defaultStep;

  // Additional validation for relisting flow
  if (flags.canAccessRelistingFlow && !isValidRelistingStep(validatedStep, flags.canAccessPostingPathTourScheduling)) {
    validatedStep = defaultStep;
    return {
      stepKey: validatedStep,
      shouldSave: !isFeed,
    };
  }

  return {
    stepKey: validatedStep,
    shouldSave: false, // Step is valid, no need to save
  };
}
